/* eslint-disable react/prop-types */
import React from 'react';
import {Button} from '@mui/material';
import {ArrowForward} from '@mui/icons-material';

const C = {
  azul: '#06448e',
  azulD: '#04316a',
  laranja: '#FF6600',
  laranjaD: '#cc4400',
  roxo: '#7c53e3',
  text: '#1a1a2e',
  muted: '#8a94a6',
  success: '#059669',
  danger: '#e53935',
};

// ─── Options ──────────────────────────────────────────────────────────────────
const OPTIONS = [
  {
    id: 'mototaxista',
    vehicleType: 'Motos',
    bag: false,
    emoji: '🏍️',
    label: 'Viagens de moto',
    sublabel: 'Mototaxista',
    desc: 'Transporte de passageiros por moto — mototáxi ou aplicativo de moto.',
    pills: ['Categoria A ou AB', 'Moto 2009+', 'Placa e CRLV'],
    color: C.azul,
    bg: '#eef6ff',
    border: '#bfdbfe',
  },
  {
    id: 'motorista',
    vehicleType: 'Carros',
    bag: false,
    emoji: '🚗',
    label: 'Viagens de carro',
    sublabel: 'Motorista de app',
    desc: 'Transporte de passageiros por carro — app de carona ou táxi.',
    pills: ['Categoria B ou AB', 'Carro 2009+', 'Placa e CRLV'],
    color: C.roxo,
    bg: '#f5f3ff',
    border: '#ddd6fe',
  },
  {
    id: 'entregador',
    vehicleType: 'Motos', // default; vehicle step will confirm
    bag: true,
    emoji: '📦',
    label: 'Entregas',
    sublabel: 'Entregador',
    desc: 'Entrega de pedidos e mercadorias por moto ou bicicleta elétrica.',
    pills: ['Moto ou bike elétrica', 'Bolsa de entrega', 'Categoria A ou AM'],
    color: C.laranja,
    bg: '#fff7ed',
    border: '#fed7aa',
  },
];

// ──────────────────────────────────────────────────────────────────────────────
// STEP INTENÇÃO
// ──────────────────────────────────────────────────────────────────────────────
/**
 * Props
 * ─────
 * sm            boolean
 * intencao      string   current selection
 * setIntencao   fn
 * setVehicleType fn
 * onNext        () => void
 * onBack        () => void   (null = oculta botão voltar)
 */
const StepIntencao = ({
  sm,
  intencao,
  setIntencao,
  setVehicleType,
  onNext,
  onBack,
}) => {
  const selected = OPTIONS.find(o => o.id === intencao) ?? null;

  const handleSelect = opt => {
    setIntencao(opt.id);
    setVehicleType(opt.vehicleType);
  };

  return (
    <div>
      <p
        style={{
          margin: '0 0 6px',
          fontSize: 15,
          fontWeight: 800,
          color: C.text,
        }}
      >
        O que você quer fazer na plataforma?
      </p>
      <p
        style={{
          margin: '0 0 20px',
          fontSize: 13,
          color: C.muted,
          lineHeight: 1.55,
        }}
      >
        Isso define os documentos que precisamos e as categorias disponíveis na
        sua região.
      </p>

      {/* Cards */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          marginBottom: 24,
        }}
      >
        {OPTIONS.map(opt => {
          const sel = intencao === opt.id;
          return (
            <button
              key={opt.id}
              onClick={() => handleSelect(opt)}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: sm ? '14px 16px' : '18px 20px',
                borderRadius: 16,
                border: `2px solid ${sel ? opt.color : opt.border}`,
                background: sel ? opt.bg : '#fff',
                cursor: 'pointer',
                transition: 'all 0.18s ease',
                fontFamily: "'Outfit','Segoe UI',sans-serif",
                boxShadow: sel
                  ? `0 4px 20px ${opt.color}22`
                  : '0 1px 4px rgba(0,0,0,0.05)',
                outline: 'none',
              }}
            >
              <div style={{display: 'flex', alignItems: 'flex-start', gap: 14}}>
                {/* Emoji bubble */}
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 14,
                    flexShrink: 0,
                    background: sel ? opt.color : opt.border,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 26,
                    transition: 'background 0.18s',
                  }}
                >
                  {opt.emoji}
                </div>

                <div style={{flex: 1, minWidth: 0}}>
                  {/* Title row */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginBottom: 3,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 16,
                        fontWeight: 800,
                        color: sel ? opt.color : C.text,
                      }}
                    >
                      {opt.label}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: 20,
                        background: sel ? `${opt.color}18` : '#f4f6fa',
                        color: sel ? opt.color : C.muted,
                      }}
                    >
                      {opt.sublabel}
                    </span>
                  </div>

                  {/* Description */}
                  <p
                    style={{
                      margin: '0 0 10px',
                      fontSize: 12,
                      color: C.muted,
                      lineHeight: 1.5,
                    }}
                  >
                    {opt.desc}
                  </p>

                  {/* Pills */}
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: 5}}>
                    {opt.pills.map(p => (
                      <span
                        key={p}
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          padding: '3px 8px',
                          borderRadius: 20,
                          background: sel ? `${opt.color}14` : '#f0f2f5',
                          color: sel ? opt.color : C.muted,
                          transition: 'all 0.15s',
                        }}
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Check indicator */}
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    flexShrink: 0,
                    border: `2px solid ${sel ? opt.color : '#e0e5ef'}`,
                    background: sel ? opt.color : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    color: '#fff',
                    transition: 'all 0.18s',
                    marginTop: 2,
                  }}
                >
                  {sel && '✓'}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Info callout if selected */}
      {selected && (
        <div
          style={{
            padding: '12px 16px',
            borderRadius: 12,
            marginBottom: 20,
            background: `${selected.color}08`,
            border: `1px solid ${selected.color}30`,
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
          }}
        >
          <span style={{fontSize: 16, flexShrink: 0}}>ℹ️</span>
          <p style={{margin: 0, fontSize: 12, color: C.text, lineHeight: 1.55}}>
            {selected.id === 'mototaxista' &&
              'Você precisará enviar foto da CNH com categoria A ou AB e com EAR (Exerce Atividade Remunerada) no campo de observações.'}
            {selected.id === 'motorista' &&
              'Você precisará enviar foto da CNH com categoria B ou AB e documentação do veículo (CRLV) atualizada.'}
            {selected.id === 'entregador' &&
              'O tipo de veículo (moto ou bike elétrica) é definido na etapa de veículo. Entregas são ativadas via chave "bag" no app.'}
          </p>
        </div>
      )}

      {/* Nav */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          justifyContent: onBack ? 'space-between' : 'flex-end',
          alignItems: 'center',
        }}
      >
        {onBack && (
          <Button
            onClick={onBack}
            style={{textTransform: 'none', color: C.muted, fontSize: 14}}
          >
            ← Voltar
          </Button>
        )}
        <Button
          onClick={onNext}
          disabled={!selected}
          endIcon={<ArrowForward />}
          sx={{
            bgcolor: selected ? selected.color : '#e0e0e0',
            color: '#fff',
            borderRadius: '12px',
            height: 48,
            px: 3,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 15,
            ':hover': {bgcolor: selected ? `${selected.color}dd` : '#e0e0e0'},
            ':disabled': {bgcolor: '#e0e0e0', color: '#aaa'},
            transition: 'background 0.2s',
          }}
        >
          Continuar
        </Button>
      </div>
    </div>
  );
};

export default StepIntencao;
